import React, { useEffect, useState } from "react";
import { useSuperAdminFetch } from "@/contexts/SuperAdminContext";
import { Bot, TrendingUp, Coins, Users2, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

interface TenantUsage {
  accountId: number;
  name: string;
  schemaName: string;
  totalPrompts: number;
  totalTokens: number;
  estimatedCostUsd: number;
}

interface TrendRow {
  date: string;
  prompt_count: number;
  total_tokens: number;
  unique_users: number;
  estimated_cost_usd: string;
}

interface OverviewData {
  totalPrompts: number;
  totalTokens: number;
  estimatedCostUsd: number;
  byTenant: TenantUsage[];
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">{label}</p>
          <p className="text-2xl font-bold text-[#e6edf3]">{value}</p>
          {sub && <p className="text-xs text-white/35 mt-1">{sub}</p>}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color} flex-shrink-0`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </div>
  );
}

function TenantRow({ tenant, apiFetch }: { tenant: TenantUsage; apiFetch: (url: string) => Promise<any> }) {
  const [expanded, setExpanded] = useState(false);
  const [trend, setTrend] = useState<TrendRow[] | null>(null);
  const [loadingTrend, setLoadingTrend] = useState(false);

  async function toggle() {
    if (!expanded && trend === null) {
      setLoadingTrend(true);
      try {
        const data = await apiFetch(`/api/superadmin/ai-usage/${tenant.accountId}`);
        setTrend(data.trend || []);
      } catch {
        setTrend([]);
      } finally {
        setLoadingTrend(false);
      }
    }
    setExpanded((v) => !v);
  }

  const chartData = (trend ?? []).map((r) => ({
    date: new Date(r.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    prompts: r.prompt_count,
    tokens: r.total_tokens,
    users: r.unique_users,
  }));

  return (
    <>
      <tr
        className="border-b border-white/[0.05] hover:bg-white/[0.02] cursor-pointer transition-colors"
        onClick={toggle}
      >
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600/30 to-indigo-600/30 border border-violet-500/20 flex-shrink-0">
              <span className="text-xs font-bold text-violet-300">
                {tenant.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#e6edf3]">{tenant.name}</p>
              <p className="text-[11px] text-white/35 font-mono">{tenant.schemaName}</p>
            </div>
          </div>
        </td>
        <td className="px-5 py-3.5 text-sm text-[#e6edf3] tabular-nums">
          {tenant.totalPrompts.toLocaleString()}
        </td>
        <td className="px-5 py-3.5 text-sm text-[#e6edf3] tabular-nums">
          {tenant.totalTokens.toLocaleString()}
        </td>
        <td className="px-5 py-3.5 text-sm tabular-nums">
          {tenant.estimatedCostUsd > 0 ? (
            <span className="text-emerald-400">${tenant.estimatedCostUsd.toFixed(4)}</span>
          ) : (
            <span className="text-white/30">—</span>
          )}
        </td>
        <td className="px-5 py-3.5">
          <div className="flex justify-end">
            {expanded
              ? <ChevronUp className="h-4 w-4 text-white/40" />
              : <ChevronDown className="h-4 w-4 text-white/40" />}
          </div>
        </td>
      </tr>

      {/* Expanded trend chart */}
      {expanded && (
        <tr className="border-b border-white/[0.05]">
          <td colSpan={5} className="px-5 py-4 bg-white/[0.015]">
            {loadingTrend ? (
              <div className="flex items-center gap-3 py-4 text-sm text-white/40">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading trend data…
              </div>
            ) : chartData.length === 0 ? (
              <p className="py-4 text-sm text-white/30 italic">No usage data in the last 30 days.</p>
            ) : (
              <div>
                <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">
                  30-day usage trend — {tenant.name}
                </p>
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                        width={32}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#161b22",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: "8px",
                          color: "#e6edf3",
                          fontSize: 12,
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="prompts"
                        name="Prompts"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4, fill: "#8b5cf6" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="users"
                        name="Unique Users"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={false}
                        strokeDasharray="4 2"
                        activeDot={{ r: 4, fill: "#6366f1" }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function AIUsage() {
  const apiFetch = useSuperAdminFetch();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const result = await apiFetch("/api/superadmin/ai-usage");
      setData(result as OverviewData);
    } catch (err: any) {
      setError(err.message || "Failed to load AI usage data.");
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const totalActiveAccounts = data?.byTenant.filter((t) => t.totalPrompts > 0).length ?? 0;

  return (
    <div className="p-8 space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-700/30">
            <Bot className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#e6edf3]">AI Usage</h1>
            <p className="text-sm text-white/40 mt-0.5">Last 30 days · across all tenants</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="border-white/10 bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/8 gap-2"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stat Cards */}
      {loading ? (
        <div className="grid grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
              <Skeleton className="h-3 w-24 bg-white/[0.06] mb-3" />
              <Skeleton className="h-7 w-20 bg-white/[0.06]" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-5 py-4 text-sm text-red-400">
          {error}
        </div>
      ) : data ? (
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            icon={TrendingUp}
            label="Total Prompts"
            value={data.totalPrompts.toLocaleString()}
            sub={`${totalActiveAccounts} active tenant${totalActiveAccounts !== 1 ? "s" : ""}`}
            color="bg-gradient-to-br from-violet-600 to-violet-700"
          />
          <StatCard
            icon={Coins}
            label="Total Tokens"
            value={data.totalTokens.toLocaleString()}
            sub="gpt-4o-mini"
            color="bg-gradient-to-br from-indigo-600 to-indigo-700"
          />
          <StatCard
            icon={Users2}
            label="Est. Cost (USD)"
            value={`$${data.estimatedCostUsd.toFixed(4)}`}
            sub="~$0.15 / 1M input tokens"
            color="bg-gradient-to-br from-emerald-600 to-emerald-700"
          />
        </div>
      ) : null}

      {/* Per-Tenant Table */}
      {!loading && !error && data && (
        <div className="rounded-xl border border-white/[0.08] overflow-hidden bg-white/[0.02]">
          <div className="px-5 py-4 border-b border-white/[0.06]">
            <p className="text-sm font-semibold text-[#e6edf3]">Per-Tenant Breakdown</p>
            <p className="text-xs text-white/35 mt-0.5">Click a row to expand the 30-day trend chart</p>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                {["Tenant", "Prompts", "Tokens", "Est. Cost", ""].map((h) => (
                  <th
                    key={h}
                    className="px-5 py-3 text-left text-[10px] font-semibold text-white/35 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.byTenant.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-sm text-white/30 italic">
                    No tenants found.
                  </td>
                </tr>
              ) : (
                data.byTenant.map((tenant) => (
                  <TenantRow key={tenant.accountId} tenant={tenant} apiFetch={apiFetch} />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
